import { useEffect } from 'react';
import { io } from 'socket.io-client';
import { env } from '@shared/config/env';
import { api } from '@shared/api';
import { getToken, hardLogout } from '@shared/auth';

/**
 * Connects to the API's authenticated Socket.IO root namespace and calls hardLogout()
 * when a session_revoked event arrives (e.g. another device revoked this web session).
 * Follows the same redis-emitter → redis-adapter pattern used for NOTIFICATION events.
 */
export function useSessionRevocation(): void {
  useEffect(() => {
    if (!getToken()) return;

    const unmountedRef = { current: false };
    let disconnect: (() => void) | null = null;

    void (async () => {
      try {
        const res = await api.get('api/v1/auth/socket-token').json<{ data: { token: string } }>();
        if (unmountedRef.current) return;

        const socketUrl =
          env.VITE_API_BASE_URL || (typeof window !== 'undefined' ? window.location.origin : '');

        const socket = io(socketUrl, {
          auth: { token: res.data.token },
          transports: ['websocket'],
          reconnection: true,
          reconnectionDelay: 2_000,
          reconnectionAttempts: 10,
        });

        disconnect = () => socket.disconnect();

        socket.on('session_revoked', () => {
          void hardLogout();
        });
      } catch {
        // Best-effort — socket revocation is a UX enhancement, not a critical auth path
      }
    })();

    return () => {
      unmountedRef.current = true;
      disconnect?.();
    };
  }, []);
}
